import React, { useRef, useState } from "react";
import CommentIcon from "@mui/icons-material/Comment";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  type DbComment,
  useCommentDelete,
  useCommentPost,
  useCommentPut,
  useCommentsQuery,
} from "../../requests/v2/comments";
import { CustomModal } from "../CustomModal";
import CancelIcon from "@mui/icons-material/Close";
import { AlertDialog } from "../AlertDialog";
import { CustomSpinner } from "../CustomSpinner";
import { DetailedEvent } from "../../requests/matchEvents";
import { useGetUser, useUser } from "../../requests/users";
import { useQueryClient } from "@tanstack/react-query";
import { AddOrEditComment } from "./AddOrEditComment";
import { CommentPaper } from "./CommentPaper";

export const CommentModal = ({
  event,
  setEventNull,
}: {
  event: DetailedEvent | null;
  setEventNull: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: comments, isLoading: isLoadingComments } = useCommentsQuery(
    event?.id,
    event?.is_comment
  );
  const user = useUser();
  const getUser = useGetUser();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isAddCommentActive, setIsNewCommentActive] = useState(false);
  const [editingComment, setEditingComment] = useState<DbComment | null>(null);
  const isEditingOrAdding = isAddCommentActive || !!editingComment;
  const [addOrEditContent, setAddOrEditContent] = useState<string | null>(null);

  const commentPost = useCommentPost().mutate;
  const commentPut = useCommentPut().mutate;
  const commentDelete = useCommentDelete().mutate;
  const isPosting = useCommentPost().isLoading;

  type AlertDialog = {
    message: string;
    onCloseDialog: () => void;
    onConfirmDialog: () => void;
    closeText: string;
    confirmText: string;
  };
  const [alertDialog, setAlertDialog] = useState<AlertDialog | null>(null);

  if (!event) {
    return null;
  }

  const onAttemptCancelNewComment = (postConfirmAction?: () => void) => {
    const confirmAction = () => {
      setAddOrEditContent(null);
      setIsNewCommentActive(false);
      postConfirmAction?.();
    };

    if (isAddCommentActive) {
      if (addOrEditContent?.length) {
        setAlertDialog({
          message: "Discard comment?",
          onCloseDialog: () => setAlertDialog(null),
          onConfirmDialog: confirmAction,
          closeText: "Go Back",
          confirmText: "Discard",
        });
      } else {
        confirmAction();
      }
    }
  };

  const onAttemptCancelEditComment = (postConfirmAction?: () => void) => {
    const confirmAction = () => {
      setAddOrEditContent(null);
      setEditingComment(null);
      postConfirmAction?.();
    };

    if (editingComment) {
      if (editingComment.content !== addOrEditContent && addOrEditContent?.length) {
        setAlertDialog({
          message: "Discard changes?",
          onCloseDialog: () => setAlertDialog(null),
          onConfirmDialog: confirmAction,
          closeText: "Go Back",
          confirmText: "Discard",
        });
      } else {
        confirmAction();
      }
    }
  };

  const onAttemptCloseCommentsModal = () => {
    onAttemptCancelNewComment(setEventNull);
    onAttemptCancelEditComment(setEventNull);
    if (!isEditingOrAdding) {
      setEventNull();
    }
  };

  const onPost = () => {
    if (!event.id) {
      console.error("Failed to post comment due to unknown event.id.", event);
      return;
    }
    if (addOrEditContent?.length) {
      commentPost(
        {
          eventId: event.id,
          content: addOrEditContent,
        },
        {
          onSuccess: () => {
            // Force a refetch of comments to ensure we get the latest data.
            // This is needed because the event.is_comment value might not be updated immediately.
            void queryClient.invalidateQueries(["comments", event.id]);
            // Also invalidate events to update the is_comment value.
            void queryClient.invalidateQueries(["detailedEvent"]);
          },
        }
      );
      setIsNewCommentActive(false);
      setAddOrEditContent(null);
    }
  };

  const onUpdate = () => {
    if (!editingComment) {
      console.error("Failed to update comment due to unknown editingComment.");
      return;
    }
    if (editingComment.content !== addOrEditContent && addOrEditContent?.length) {
      commentPut(
        { ...editingComment, content: addOrEditContent },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries(["comments", event.id]);
            void queryClient.invalidateQueries(["detailedEvent"]);
          },
        }
      );
      setEditingComment(null);
      setAddOrEditContent(null);
    }
  };

  const onAttemptDelete = (comment: DbComment) => {
    setAlertDialog({
      message: "Delete comment?",
      onCloseDialog: () => setAlertDialog(null),
      onConfirmDialog: () => {
        commentDelete(
          {
            id: comment.id,
            eventId: comment.eventId,
            externalCommentId: comment.externalCommentId,
          },
          {
            onSuccess: () => {
              void queryClient.invalidateQueries(["comments", event.id]);
              void queryClient.invalidateQueries(["detailedEvent"]);
            },
          }
        );
        setEditingComment(null);
      },
      closeText: "Go Back",
      confirmText: "Delete",
    });
  };

  return (
    <CustomModal
      open={!!event}
      // Explicitly stopping propagation in this is necessary. For some reason, clicks on this element are bubbling
      // down into the EventCard component.
      onClick={(e) => e.stopPropagation()}
      onClose={onAttemptCloseCommentsModal}
    >
      <>
        <AlertDialog
          isOpen={!!alertDialog}
          message={alertDialog?.message}
          onCloseDialog={alertDialog?.onCloseDialog}
          onConfirmDialog={alertDialog?.onConfirmDialog}
          closeText={alertDialog?.closeText}
          confirmText={alertDialog?.confirmText}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "90%" : "35em",
            maxWidth: isMobile ? "400px" : "none",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            pt: 4,
            px: 4,
            maxHeight: "80vh",
            minHeight: "250px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {comments ? (
            <Box
              sx={{
                maxHeight: "300px",
                overflowY: "auto",
                mb: 2,
              }}
            >
              {comments.length === 0 && !isAddCommentActive && !isPosting ? (
                <Box
                  sx={{
                    height: "120px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Typography variant="body1" color="textSecondary">
                    No comments yet
                  </Typography>
                </Box>
              ) : (
                <>
                  {comments.map((comment) =>
                    comment.externalCommentId === editingComment?.externalCommentId ? (
                      <AddOrEditComment
                        key={comment.externalCommentId}
                        onClose={() => onAttemptCancelEditComment()}
                        onSubmit={onUpdate}
                        addOrEditContent={addOrEditContent}
                        setAddOrEditContent={setAddOrEditContent}
                        inputRef={inputRef}
                      />
                    ) : (
                      <CommentPaper
                        key={comment.id}
                        comment={comment}
                        user={user}
                        getUser={getUser}
                        isEditingOrAdding={isEditingOrAdding}
                        setAddOrEditContent={setAddOrEditContent}
                        setEditingComment={setEditingComment}
                        onAttemptDelete={onAttemptDelete}
                      />
                    )
                  )}
                </>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                height: "120px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {isLoadingComments ? (
                <CustomSpinner />
              ) : !isAddCommentActive && !isPosting ? (
                <>
                  <Typography variant="body1" color="textSecondary">
                    No comments yet
                  </Typography>
                </>
              ) : null}
            </Box>
          )}
          {isAddCommentActive && (
            <AddOrEditComment
              onClose={() => onAttemptCancelNewComment()}
              onSubmit={onPost}
              addOrEditContent={addOrEditContent}
              setAddOrEditContent={setAddOrEditContent}
              inputRef={inputRef}
            />
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 2,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              disabled={isEditingOrAdding || isPosting}
              onClick={() => {
                setAddOrEditContent("");
                setIsNewCommentActive(true);
              }}
              startIcon={isPosting ? <CustomSpinner /> : <CommentIcon />}
              variant="outlined"
              color="primary"
              sx={{ mx: 0.5, my: 2 }}
            >
              {isPosting ? "Posting..." : "Add Comment"}
            </Button>
            <Button
              onClick={onAttemptCloseCommentsModal}
              startIcon={<CancelIcon />}
              variant="outlined"
              color="error"
              sx={{ mx: 0.5, my: 2 }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </>
    </CustomModal>
  );
};
